import { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../common/components/Card";
import CustomForm from "../common/components/CustomForm";
import useClientConfig from "../common/hooks/clientConfigHook";
import useNetworkEffect from "../common/hooks/networkEffectHook";
import { cdate } from "../common/utils/CDate";
import { getJobImageData, useJobImages } from "../reducers/jobImageSlice";
import { useJobNotes } from "../reducers/jobNoteSlice";
import { useJobUpdates } from "../reducers/jobUpdateSlice";
import { useLevel } from "../reducers/levelSlice";
import { useProblem } from "../reducers/problemSlice";
import { useSite } from "../reducers/siteSlice";
import { useType } from "../reducers/typeSlice";

import PropTypes from 'prop-types';
import DataTable from "../common/components/DataTable";
import { Col, Row } from "../common/components/Grid";
import Checkbox from "../common/components/Checkbox";
import { useJobRelatedDocs } from "../reducers/jobRelatedDocSlice";
import { readField, writeField } from "../common/utils/objectUtilities";
import TextInput from "../common/components/TextInput";
import { useSpecializedTranslation } from "../hooks/specializedTranslationHook";
import useNetwork from "../common/hooks/networkHook";
import { updateJob, useJob } from "../reducers/jobSlice";

const JobUpdate = ({ jobid, config, update, onChange }) => {
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const job = useJob(jobid);
    
    const { t } = useSpecializedTranslation(job);
    const jobImages = useJobImages(job.jobid);
    const jobNotes = useJobNotes(job.jobid);
    const jobRelatedDocs = useJobRelatedDocs(job.jobid);
    const jobUpdates = useJobUpdates(job.jobid);
    const site = useSite(job?.site);
    const level = useLevel(job?.site, job?.level);
    const type = useType(job?.site, job?.level, job?.type);
    const problem = useProblem(job?.site, job?.level, job?.type, job?.problem);
    const [thumbnailData, setThumbnailData] = useState({});
    const [reason, setReason] = useState(update?.reason);
    const [showMap, setShowMap] = useState(update?.show_map ?? config.show_map ?? false);
    const [ selectedImages, setSelectedImages ] = useState(update?.jobimages ? update.jobimages.reduce((acc, img) => { acc[img.id] = true; return acc; }, {}) : {});
    const [ selectedNotes, setSelectedNotes ] = useState(update?.jobnotes ? update.jobnotes.reduce((acc, note) => { acc[note.id] = true; return acc; }, {}) : {});
    
    const selectableNotes = useMemo(() => {
        const prev = jobUpdates?.find(update => update.kindid == config.id);
        if (prev) {
            const lastupdate = cdate(prev.timestamp, clientConfig.tz);
            return jobNotes.filter(note => cdate(note.timestamp, clientConfig.tz).isAfter(lastupdate) && !note.strike_timestamp);
        }
        return jobNotes;
    }, [jobNotes, jobUpdates, clientConfig.tz, config.id]);

    const notesTable = useMemo(() => [
        { title: t('dateTime'), type: 'date', key: 'date_timestamp', field: 'timestamp', editable: false, readonly: true },
        { title: t('timeOfDay'), type: 'time', key: 'time_timestamp', field: 'timestamp', editable: false, readonly: true },
        { title: t('text'), type: 'textarea', rowspan: 3, field: 'note', editable: false }
    ], [t]);

    const rawCustomfields = useMemo(() => {
        const fields = [type?.formdef ?? [], site?.formdef ?? [], level?.formdef ?? [], problem?.formdef ?? []].flat().map(section => section.rows.flat()).flat().filter(f => f.field).map(f => ({ ...f, field: 'customfields.' + f.field}));
        return fields.concat((clientConfig.jobGeneralFormSpec ?? []).map(section => section.rows).flat().filter(f => f.field).map(f => ({ ...f, field: 'generalcustomfields.' + f.field })))
    }, [type?.formdef, site?.formdef, level?.formdef, problem?.formdef, clientConfig.jobGeneralFormSpec])
    const updateCustomfields = useMemo(() => rawCustomfields.filter(f => config.fields?.some(cf => cf === f.field)), [config?.fields, rawCustomfields]);
    const updateFieldDefs = useMemo(() => [{ borderless: true, rows: updateCustomfields.map(field => [ {...field, colspan: 12 } ]) }], [updateCustomfields]);

    const selectableImages = useMemo(() => {
        const prev = jobUpdates?.find(update => update.kindid == config.id);
        if (prev) {
            return jobImages.filter(img => prev.jobimages.every(i => i.id !== img.id));
        }
        return jobImages;
    }, [jobImages, jobUpdates, config.id]);
    

    useNetworkEffect(network => {
        jobImages.filter(img => !thumbnailData[img.id]).forEach(async img => {
            let data = await getJobImageData(network, img.jobid, img.filename, 300);
            setThumbnailData(thumbnailData => ({ ...thumbnailData, [img.id]: data }));
        });
    }, [jobImages]);

    useEffect(() => {
        (async () => {
            onChange?.({
                jobid,
                kindid: config.id,
                reason,
                job_fields: config.fields.reduce((obj, field) => { writeField(obj, field, readField(job, field)); return obj; }, {}),
                show_map: showMap,
                jobnotes: jobNotes.filter(n => selectedNotes[n.id]),
                jobimages: jobImages.filter(i => selectedImages[i.id.toString()]),//.map(img => ({ ...img, data: thumbnailData[img.id] })),
                jobrelateddocs: jobRelatedDocs,
                updatenumber: jobUpdates?.length > 0 ? (Math.max.apply(null, jobUpdates.filter(update => update.kindid == config.id).map(p => p.updatenumber)) + 1) : 1,
                job
            })
        })();
    }, [jobUpdates, job, jobRelatedDocs, jobImages, jobNotes, selectedImages, selectedNotes, showMap, config.fields, config.id, onChange, reason, jobid])

    const handleJobUpdate = useCallback(delta => {
        updateJob(network, { ...delta, jobid })
    }, [network, jobid]);
    
    const handleNoteSelect = useCallback(selections => {
        setSelectedNotes(selections.reduce((acc, id) => { acc[id] = true; return acc; }, {}));
    }, []);

    return <>
        <Row>
            <Col>
                <TextInput value={reason} live={true} onChange={setReason} placeholder={t('reason')}></TextInput>
            </Col>
        </Row>
        { config.allowEditing ? <><Row>
            <Col>
                <Checkbox checked={showMap} onChange={() => setShowMap(showMap => !showMap)} placeholder={t('showMap')}></Checkbox>
            </Col>
        </Row>
        <Card title={t('jobDetails')}>
            <CustomForm def={updateFieldDefs} data={job} onChange={handleJobUpdate} required={true}></CustomForm>
        </Card>
        <Card title={t('jobNotes')}>
            <DataTable columns={notesTable} value={selectableNotes} selections={selectedNotes} disableAdd={true} disableRemove={true} pkey="id" onSelect={handleNoteSelect} defaultSort={['timestamp']} emptyMessage={t('notesEmpty')}></DataTable>
        </Card>
        <Card title={t('jobImages')}>
        <div style={{ display: "inline-flex", flexDirection: "row", flexWrap: "wrap"}}>
                {selectableImages?.length > 0 ? selectableImages.map(image => { 
                    return <div key={job.displayid + '_' + image.id} className="m-2" style={{ maxWidth: '200px'}}>
                        <Row>
                            <Col>
                                {thumbnailData[image.id] ? 
                                    <div style={{ width: '202px', height: '202px', position: 'relative', border: '1px solid black'}}>
                                        <div style={{ position: 'absolute', zIndex: 5, top: '10px', left: '10px' }}>
                                            <Checkbox checked={!!selectedImages[image.id.toString()]} onChange={() => setSelectedImages(selectedImages => ({ ...selectedImages, [image.id.toString()]: !selectedImages[image.id.toString()] }))}></Checkbox>
                                        </div>
                                        <img src={thumbnailData[image.id]} alt={image.caption} style={{objectFit: 'contain', width: '200px', height: '200px'}}></img>
                                    </div>
                                : t('loading')}
                            </Col>
                        </Row>
                        { clientConfig.disableImageCaption ? null : <Row>
                            <Col>
                                {image.caption}
                            </Col>
                        </Row> }
                    </div>
                }) : <div>{t('noImages')}</div>}
            </div>
        </Card></> : null }
    </>

}

JobUpdate.propTypes = {
    jobid: PropTypes.string.isRequired,
    config: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired
}

export default JobUpdate;